import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  MapPin, 
  FileText, 
  Download, 
  GitBranch, 
  MessageCircle, 
  Scale, 
  Users,
  Shield,
  Heart,
  ArrowRight,
  AlertTriangle,
  HandHeart,
  Quote,
  Home as HomeIcon
} from 'lucide-react';
import 'leaflet/dist/leaflet.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fix for default markers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const createIcon = (color) => new L.Icon({
  iconUrl: `https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-${color}.png`,
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const icons = {
  urgent: createIcon('red'),
  en_cours: createIcon('orange'),
  resolu: createIcon('green')
};

const services = [
  {
    icon: MapPin,
    title: 'Cartographie',
    description: 'Visualisez les situations en cours et trouvez du soutien près de chez vous.',
    link: '/carte',
    color: 'text-primary'
  },
  {
    icon: FileText,
    title: 'Documentation',
    description: 'Ressources juridiques, jurisprudences et guides pour monter en compétence.',
    link: '/documentation',
    color: 'text-secondary'
  },
  {
    icon: MessageCircle,
    title: 'Assistant IA',
    description: 'Posez vos questions juridiques et obtenez des réponses immédiates.',
    link: '/assistant',
    color: 'text-primary'
  },
  {
    icon: Download,
    title: 'Outils',
    description: 'Courriers types et modèles de réponse prêts à l\'emploi.',
    link: '/outils',
    color: 'text-secondary'
  },
  {
    icon: GitBranch,
    title: 'Parcours',
    description: 'Comprenez où vous en êtes et visualisez les prochaines étapes.',
    link: '/parcours',
    color: 'text-primary'
  },
  {
    icon: Scale,
    title: 'Avocats',
    description: 'Trouvez un avocat spécialisé dans votre région.',
    link: '/avocats',
    color: 'text-secondary'
  }
];

export default function Home() {
  const [situations, setSituations] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [stats, setStats] = useState({
    situations: 0,
    supports: 0,
    lawyers: 0
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [situationsRes, testimonialsRes, lawyersRes] = await Promise.all([
        axios.get(`${API}/situations`),
        axios.get(`${API}/testimonials`),
        axios.get(`${API}/lawyers`)
      ]);
      setSituations(situationsRes.data);
      setTestimonials(testimonialsRes.data.slice(0, 2));
      setStats({
        situations: situationsRes.data.length,
        supports: situationsRes.data.reduce((acc, s) => acc + s.support_count, 0),
        lawyers: lawyersRes.data.length
      });
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  };

  const urgentSituations = situations.filter(s => s.status === 'urgent');

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{ 
            backgroundImage: `url('https://images.unsplash.com/photo-1767284933117-70a1cba02bf0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHx3b29kZW4lMjBjYWJpbiUyMGZvcmVzdCUyMG5hdHVyZSUyMHBlYWNlZnVsfGVufDB8fHx8MTc3Mzc3Nzk3NHww&ixlib=rb-4.1.0&q=85')` 
          }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-foreground mb-6 animate-fade-in-up">
              Défendons ensemble le droit à l'
              <span className="text-primary">habitat léger</span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground mb-8 animate-fade-in-up animation-delay-100">
              Une plateforme pour accompagner, mutualiser et structurer un véritable réseau de défense 
              pour celles et ceux qui ont choisi de vivre autrement.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up animation-delay-200">
              <Link to="/carte" data-testid="hero-cta-map">
                <Button size="lg" className="gap-2 w-full sm:w-auto">
                  <MapPin className="w-5 h-5" />
                  Voir la carte
                </Button>
              </Link>
              <Link to="/contact" data-testid="hero-cta-contact">
                <Button size="lg" variant="outline" className="gap-2 w-full sm:w-auto">
                  <MessageCircle className="w-5 h-5" />
                  Contacter le collectif
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Urgent Call to Action */}
      <section className="bg-primary text-primary-foreground py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-primary-foreground/20 flex items-center justify-center shrink-0">
                <AlertTriangle className="w-7 h-7" />
              </div>
              <div>
                <h2 className="text-xl md:text-2xl font-bold">Besoin d'aide face à une procédure ?</h2>
                <p className="opacity-90">Inscrivez-vous sur la carte et recevez le soutien de notre communauté.</p>
              </div>
            </div>
            <div className="flex gap-3">
              <Link to="/carte?action=add" data-testid="urgent-cta-register">
                <Button size="lg" variant="secondary" className="gap-2">
                  <MapPin className="w-5 h-5" />
                  M'inscrire sur la carte
                </Button>
              </Link>
              <Link to="/carte" data-testid="urgent-cta-support">
                <Button size="lg" variant="outline" className="gap-2 border-primary-foreground text-primary-foreground hover:bg-primary-foreground/10">
                  <HandHeart className="w-5 h-5" />
                  Soutenir une cause
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-muted/50 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-3 gap-8 text-center">
            <div>
              <div className="text-3xl md:text-4xl font-bold text-primary">{stats.situations}+</div>
              <div className="text-sm text-muted-foreground mt-1">Situations référencées</div>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-bold text-primary">{stats.supports}+</div>
              <div className="text-sm text-muted-foreground mt-1">Soutiens apportés</div>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-bold text-primary">{stats.lawyers}</div>
              <div className="text-sm text-muted-foreground mt-1">Avocats partenaires</div>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Map Preview */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
              Carte des situations en France
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Visualisez les situations en cours et trouvez comment aider près de chez vous.
            </p>
          </div>
          
          <Card className="overflow-hidden">
            <div className="h-[400px] relative" data-testid="home-map-preview">
              <MapContainer
                center={[46.603354, 1.888334]}
                zoom={5}
                style={{ height: '100%', width: '100%', zIndex: 0 }}
                scrollWheelZoom={false}
                dragging={true}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {situations.map((situation) => (
                  <Marker
                    key={situation.id}
                    position={[situation.latitude, situation.longitude]}
                    icon={icons[situation.status] || icons.en_cours}
                  >
                    <Popup>
                      <div className="min-w-[200px]">
                        <h4 className="font-bold text-sm mb-1">{situation.title}</h4>
                        <p className="text-xs text-muted-foreground mb-2">{situation.address}</p>
                        <Link to={`/carte?situation=${situation.id}`}>
                          <Button size="sm" className="w-full">Voir les détails</Button>
                        </Link>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
              
              {/* Overlay with urgent situations */}
              {urgentSituations.length > 0 && (
                <div className="absolute top-4 left-4 z-[1000] bg-white/95 backdrop-blur rounded-lg shadow-lg p-4 max-w-xs">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="w-4 h-4 text-destructive" />
                    <span className="font-semibold text-sm">Situations urgentes</span>
                  </div>
                  <div className="space-y-2">
                    {urgentSituations.slice(0, 2).map(s => (
                      <div key={s.id} className="text-xs">
                        <span className="font-medium">{s.title}</span>
                        <span className="text-muted-foreground"> - {s.support_count} soutiens</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            <CardContent className="py-4 bg-muted/30 flex justify-center gap-4">
              <Link to="/carte?action=add">
                <Button className="gap-2">
                  <MapPin className="w-4 h-4" />
                  Référencer ma situation
                </Button>
              </Link>
              <Link to="/carte">
                <Button variant="outline" className="gap-2">
                  <HandHeart className="w-4 h-4" />
                  Soutenir une cause
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 md:py-24 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
              Nos outils à votre service
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Des ressources concrètes pour vous accompagner à chaque étape de votre parcours.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <Link key={index} to={service.link} data-testid={`service-card-${service.link.replace('/', '')}`}>
                <Card className="h-full hover:shadow-lg transition-shadow duration-200 border-border/60">
                  <CardHeader>
                    <div className={`w-12 h-12 rounded-full bg-accent flex items-center justify-center mb-2 ${service.color}`}>
                      <service.icon className="w-6 h-6" />
                    </div>
                    <CardTitle className="text-xl">{service.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-base">{service.description}</CardDescription>
                    <div className="mt-4 flex items-center text-primary text-sm font-medium">
                      En savoir plus <ArrowRight className="w-4 h-4 ml-1" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      {testimonials.length > 0 && (
        <section className="py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
                Ils ont traversé ces épreuves
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Des témoignages de personnes qui ont fait face aux procédures et s'en sont sorties.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {testimonials.map((testimonial) => (
                <Card key={testimonial.id} className="bg-accent/20">
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                        <Quote className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-semibold">{testimonial.anonymous ? 'Anonyme' : testimonial.name}</span>
                          <Badge variant="outline">{testimonial.location}</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground italic line-clamp-3">
                          "{testimonial.testimonial}"
                        </p>
                        {testimonial.outcome && (
                          <p className="mt-2 text-sm text-green-600 font-medium">
                            → {testimonial.outcome}
                          </p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            
            <div className="text-center">
              <Link to="/temoignages">
                <Button variant="outline" className="gap-2">
                  Voir tous les témoignages
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Mission Section */}
      <section className="py-16 md:py-24 bg-accent/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6">
                Notre mission
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  Face à la multiplication des procédures contre les habitats légers, 
                  nous avons créé cet espace de solidarité et de ressources.
                </p>
                <p>
                  Yourtes, tiny houses, cabanes, caravanes... Ces modes de vie alternatifs 
                  sont souvent confrontés à une administration hostile et à un vide juridique.
                </p>
                <p>
                  Ensemble, nous pouvons nous défendre, partager nos expériences 
                  et construire un avenir où chacun peut vivre selon ses choix.
                </p>
              </div>
              <div className="mt-8 flex flex-wrap gap-4">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Users className="w-5 h-5 text-primary" />
                  </div>
                  <span className="font-medium">Solidarité</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Shield className="w-5 h-5 text-primary" />
                  </div>
                  <span className="font-medium">Défense</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Heart className="w-5 h-5 text-primary" />
                  </div>
                  <span className="font-medium">Entraide</span>
                </div>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://customer-assets.emergentagent.com/job_43abda56-5304-4174-8eba-26d8eed77bcc/artifacts/6t9selz7_groupe%20cabane%20defense.jpg"
                alt="Le collectif devant une cabane"
                className="rounded-lg shadow-lg w-full object-cover aspect-[4/3]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="bg-primary text-primary-foreground border-0">
            <CardContent className="p-8 md:p-12 text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Vous faites face à une procédure ?
              </h2>
              <p className="text-lg opacity-90 mb-8 max-w-2xl mx-auto">
                Référencez votre situation sur notre carte et bénéficiez du soutien 
                de notre communauté. Vous n'êtes pas seul·e.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/inscription" data-testid="cta-register">
                  <Button size="lg" variant="secondary" className="gap-2 w-full sm:w-auto">
                    Créer un compte
                  </Button>
                </Link>
                <a 
                  href="https://www.helloasso.com/associations/desobeissance-fertile/formulaires/1"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="cta-donate"
                >
                  <Button size="lg" variant="outline" className="gap-2 w-full sm:w-auto border-primary-foreground text-primary-foreground hover:bg-primary-foreground/10">
                    <Heart className="w-5 h-5" />
                    Soutenir le collectif
                  </Button>
                </a>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
}
